/**
 * Logo Cache Manager
 * Handles caching of store logos to localStorage for faster subsequent loads
 */

const LOGO_CACHE_KEY = 'storeLogoCache';
const COLORS_CACHE_KEY = 'storeColorsCache';

export interface CachedLogo {
  url: string;
  storeName: string;
  timestamp: number;
}

export interface CachedColors {
  primary: string;
  background: string;
  text: string;
  button: string;
  buttonText: string;
  timestamp: number;
}

/**
 * Save logo to cache
 */
export function cacheStoreLogo(logoUrl: string, storeName: string): void {
  if (!logoUrl) return;
  
  try {
    const cacheData: CachedLogo = {
      url: logoUrl,
      storeName,
      timestamp: Date.now()
    };
    localStorage.setItem(LOGO_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache logo:', error);
  }
}

/**
 * Get cached logo
 */
export function getCachedLogo(): CachedLogo | null {
  try {
    const cached = localStorage.getItem(LOGO_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn('Failed to retrieve cached logo:', error);
  }
  return null;
}

/**
 * Clear logo cache
 */
export function clearLogoCache(): void {
  try {
    localStorage.removeItem(LOGO_CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear logo cache:', error);
  }
}

/**
 * Cache store colors
 */
export function cacheStoreColors(
  primary: string,
  background: string,
  text: string,
  button: string,
  buttonText: string
): void {
  try {
    const cacheData: CachedColors = {
      primary,
      background,
      text,
      button,
      buttonText,
      timestamp: Date.now()
    };
    localStorage.setItem(COLORS_CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache colors:', error);
  }
}

/**
 * Get cached colors
 */
export function getCachedColors(): CachedColors | null {
  try {
    const cached = localStorage.getItem(COLORS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (error) {
    console.warn('Failed to retrieve cached colors:', error);
  }
  return null;
}

/**
 * Clear colors cache
 */
export function clearColorsCache(): void {
  try {
    localStorage.removeItem(COLORS_CACHE_KEY);
  } catch (error) {
    console.warn('Failed to clear colors cache:', error);
  }
}
